import { Opportunity, type RequestType } from "@entities";
import { resolveOpportunityManagerId } from "../../components/add-request/resolve-opportunity-manager";
import { Button, CircularProgress, Stack } from "@mui/material";
import { opportunityRepo } from "@repositories";
import { REQUEST_DELETED_REASON, REQUEST_DELETED_REASON_HEB, WEEK_DAYS, WEME_ORGANIZATION_ID, DEFAULT_OPPORTUNITY_IMAGE_ID } from "@shared/helpers/constants";
import { ADMIN_GROUP_ADD_ONE_FOR_FEED } from "@shared/helpers/opportunity-group-admin-copy";
import { resolveVolunteerRegistrationFlowForAdmin } from "@shared/helpers/volunteer-registration-outcome";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ValidationError } from "yup";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useRequest } from "../../contexts/RequestProvider";
import useQuestion from "../../dialog-components/QuestionDialog";
import { REQUEST_VALIDATION_SCHEMA, type OrganizationRequestSaveResult } from "./OrganizationRequest";
import dayjs from "dayjs";
import { useUser } from "../../contexts/UserProvider";
import { useFormDialog } from "../../dialog-components/FormDialog";

interface OrganizationRequestButtonsProps {
  isEdit?: boolean;
  setIsAddEvent?: React.Dispatch<React.SetStateAction<boolean>>;
  /** @deprecated Use isStandalonePage */
  fullPage?: boolean;
  isStandalonePage?: boolean;
  onSaveSuccess?: (result?: OrganizationRequestSaveResult) => void;
}

function withResolvedVolunteerRegistrationFlow<T extends Partial<Opportunity>>(
  request: T,
): T {
  return {
    ...request,
    volunteerRegistrationFlow: resolveVolunteerRegistrationFlowForAdmin(request),
  };
}

function withSaveGuard<T extends Partial<Opportunity>>(request: T) {
  return {
    ...request,
    expectedUpdatedAt: request.updatedAt,
  };
}

const OrganizationRequestButtons: React.FC<OrganizationRequestButtonsProps> = (
  props,
) => {
  const { setIsAddEvent, isEdit, fullPage, isStandalonePage, onSaveSuccess } = props;
  const resolvedStandalone = isStandalonePage ?? fullPage ?? false;
  const { currentUser } = useUser();
  const { setSeverity, setAlertText, setHideGeneralAlert } = useAlert();
  const question = useQuestion();
  const formDialog = useFormDialog();
  const {
    currentEvent: requestDetails,
    resetRequest,
    repeatConfig,
    setIsTouched,
    setCurrentEvent,
  } = useRequest();
  const [isProgress, setIsProgress] = React.useState(false);
  const [isEditProgress, setIsEditProgress] = React.useState(false);
  const isAidRequest = requestDetails?.status === "AID_REQUEST";
  const { currOrganization } = useOrganization();
  const [deleteReason, setDeleteReason] = React.useState<typeof REQUEST_DELETED_REASON[number] | null>(null);
  const navigate = useNavigate();

  async function handleApplyResult(
    res: { success: boolean; message: string; code?: string },
    onSuccess: () => void,
  ) {
    if (res.code === "CONFLICT") {
      setSeverity("warning");
      setAlertText(res.message);
      const syncVersion = await question(
        "לעדכן את נתוני הגרסה ולנסות שמירה שוב? השינויים שלך בטופס יישמרו.",
        "",
        { confirmText: "עדכון גרסה", cancelText: "ביטול" },
      );
      if (syncVersion && requestDetails?.id) {
        const fresh = await opportunityRepo.findId(requestDetails.id);
        if (fresh?.updatedAt) {
          setCurrentEvent((prev) => ({ ...prev, updatedAt: fresh.updatedAt }));
          setAlertText("נתוני הגרסה עודכנו. ניתן ללחוץ שמירה שוב.");
          setSeverity("info");
        }
      }
      return;
    }
    setSeverity(res.success ? "success" : "error");
    setAlertText(res.message);
    if (res.success) onSuccess();
  }

  function handlePostSave(result?: OrganizationRequestSaveResult) {
    if (resolvedStandalone) {
      if (result?.newId && requestDetails.isOpportunityGroupParent) {
        navigate(`/events/${result.newId}`, {
          state: { selectedList: "group-slots" },
        });
        return;
      }
      navigate(-1);
      return;
    }
    onSaveSuccess?.(result);
  }

  async function handleDelete() {
    if (await question("האם אתה בטוח שברצונך למחוק את הבקשה?")) {
      const res = await formDialog({
        title: "מה סיבת המחיקה?",
        fields: {
          reason: {
            type: "select",
            options: REQUEST_DELETED_REASON.map((reason) => ({
              value: reason,
              content: REQUEST_DELETED_REASON_HEB[reason],
            })),
            caption: "סיבה"
          }
        },
        defaultValues: {
          reason: deleteReason || ""
        },
        onOk: (values) => {
          setDeleteReason(values.reason as typeof REQUEST_DELETED_REASON[number]);
        }
      });

      setIsProgress(true);
      try {
        const deleteReason = res?.reason as typeof REQUEST_DELETED_REASON[number] | null;
        if (!deleteReason) {
          setAlertText("יש לבחור סיבה למחיקה");
          setSeverity("error");
          setIsProgress(false);
          return;
        }
        if (requestDetails?.id) {
          const deleteRes = await Opportunity.deleteRequest(
            requestDetails?.id,
            deleteReason,
          );
          setAlertText(deleteRes.message);
          setSeverity(deleteRes.success ? "success" : "error");
          if (deleteRes.success) {
            setIsAddEvent?.(false);
            setIsProgress(false);
            navigate("/user-requests");
            return;
          }
        }
      } catch (error: any) {
        setAlertText(error?.message || "לא ניתן להעביר את הבקשה לארכיון");
        setSeverity("error");
        setIsProgress(false);
      }
    }
  }

  async function handleUpdate() {
    const { editRepeatedType, ...request } = requestDetails;
    setIsTouched(false);
    try {
      const res = await Opportunity.applyRequestChanges(
        withSaveGuard(
          withResolvedVolunteerRegistrationFlow({
            ...request,
            managerId: resolveOpportunityManagerId(request, currentUser),
            status: "AID_REQUEST",
          }),
        ),
      );
      await handleApplyResult(res, () => {
        setIsAddEvent?.(false);
        resetRequest();
        handlePostSave();
      });
    } catch (error) {
      console.error("error: ", error);
      setAlertText("קרתה תקלה, יש לנסות שוב מאוחר יותר");
    }
  }

  async function handleSave() {
    const { associations, ...organization } = currOrganization!;
    try {
      setIsTouched(false);
      await validateFields({
        ...requestDetails,
        imageId:
          requestDetails.imageId ||
          organization.defaultOpportunityBackgroundImageId,
      });
    } catch (e: any) {
      setAlertText(
        `${opportunityRepo?.metadata?.fields?.find(e.path as string)?.caption}: ${e.message}`,
      );
      return setHideGeneralAlert(true);
    }
    setIsProgress(true);
    try {
      if (isEdit && requestDetails.id && requestDetails.action !== "duplicate") {
        const res = await Opportunity.applyRequestChanges(
          withSaveGuard(
            withResolvedVolunteerRegistrationFlow({
              ...requestDetails,
              managerId: resolveOpportunityManagerId(requestDetails, currentUser),
              status: "APPROVED",
              ...(requestDetails.status === "AID_REQUEST"
                ? { isClosed: false, isFull: false }
                : {}),
              day: requestDetails.startDate
                ? WEEK_DAYS[dayjs(requestDetails.startDate).day()]
                : undefined,
              repeatConfig,
              organization,
              isPublic: organization.id === WEME_ORGANIZATION_ID,
              isReportRequired:
                requestDetails.isReportRequired !== undefined
                  ? requestDetails.isReportRequired
                  : !!organization?.hasDefaultReport,
              imageId:
                requestDetails.imageId ||
                organization.defaultOpportunityBackgroundImageId,
            }),
          ),
        );
        await handleApplyResult(res, () => {
          setIsAddEvent?.(false);
          resetRequest();
          handlePostSave();
        });
        setIsProgress(false);
        return;
      }

      if (isAidRequest && requestDetails.id) {
        const res = await Opportunity.applyRequestChanges(
          withSaveGuard(
            withResolvedVolunteerRegistrationFlow({
              ...requestDetails,
              managerId: resolveOpportunityManagerId(requestDetails, currentUser),
              status: "APPROVED",
              isClosed: false,
              isFull: false,
              day: requestDetails.startDate ? WEEK_DAYS[dayjs(requestDetails.startDate).day()] : undefined,
              repeatConfig,
              organization,
              isPublic: organization.id === WEME_ORGANIZATION_ID,
              isReportRequired:
                requestDetails.isReportRequired !== undefined
                  ? requestDetails.isReportRequired
                  : !!organization?.hasDefaultReport,
              imageId:
                requestDetails.imageId ||
                organization.defaultOpportunityBackgroundImageId,
            }),
          ),
        );
        await handleApplyResult(res, () => {
          setIsAddEvent?.(false);
          resetRequest();
          handlePostSave();
        });
        setIsProgress(false);
      } else {
        const isDuplicate = requestDetails.action === "duplicate";
        const res = await Opportunity.saveRequest(
          withResolvedVolunteerRegistrationFlow({
            ...requestDetails,
            managerId: resolveOpportunityManagerId(requestDetails, currentUser),
            status: "APPROVED",
            day: requestDetails.startDate ? WEEK_DAYS[dayjs(requestDetails.startDate).day()] : undefined,
            repeatConfig,
            organization,
            isPublic: organization.id === WEME_ORGANIZATION_ID,
            isReportRequired:
              requestDetails.isReportRequired !== undefined
                ? requestDetails.isReportRequired
                : !!organization?.hasDefaultReport,
            imageId:
              requestDetails.imageId ||
              organization.defaultOpportunityBackgroundImageId ||
              DEFAULT_OPPORTUNITY_IMAGE_ID,
          }),
          requestDetails.status,
        );
        setSeverity(res.success ? "success" : "error");
        if (res.success && requestDetails.isOpportunityGroupParent) {
          setAlertText(
            `${res.message} ${ADMIN_GROUP_ADD_ONE_FOR_FEED}`,
          );
        } else {
          setAlertText(res.message);
        }
        if (res.success) {
          setIsAddEvent?.(false);
          resetRequest();
          const newId = res.requestOpportunity?.id;
          handlePostSave({ newId, isDuplicate });
        }
        setIsProgress(false);
      }
    } catch (error) {
      setAlertText("קרתה תקלה, יש לנסות שוב מאוחר יותר");
      setIsProgress(false);
    }
  }

  async function validateFields(requestDetails: RequestType) {
    if (requestDetails.isOpportunityGroupParent) {
      await REQUEST_VALIDATION_SCHEMA.validateAt("title", requestDetails);
      await REQUEST_VALIDATION_SCHEMA.validateAt("description", requestDetails);
      await REQUEST_VALIDATION_SCHEMA.validateAt("amount", requestDetails);
      await REQUEST_VALIDATION_SCHEMA.validateAt(
        "defaultMatchWeight",
        requestDetails,
      );
      if (!requestDetails.selectedTeams?.length) {
        throw new ValidationError("יש לבחור לפחות פרויקט אחד", null, "teams");
      }
      return;
    }

    const res = await REQUEST_VALIDATION_SCHEMA.validate(requestDetails);
    if (res) {
      if (!requestDetails.hasTime && !requestDetails.timeDescription) {
        throw new ValidationError("יש למלא תיאור זמן", null, "timeDescription");
      } else if (
        !isEdit &&
        requestDetails.hasTime &&
        requestDetails.hasStartTime &&
        requestDetails.startDate &&
        requestDetails.startDate < new Date()
      ) {
        throw new ValidationError(
          "תאריך התחלה לא יכול להיות לפני תאריך נוכחי",
          null,
          "startDate",
        );
      } else if (
        requestDetails.endDate &&
        requestDetails.startDate &&
        requestDetails.endDate < requestDetails.startDate
      ) {
        throw new ValidationError(
          "זמן הסיום לא יכול להיות לפני זמן התחלה",
          null,
          "endDate",
        );
      }
      else if (!requestDetails.selectedTeams?.length) {
        throw new ValidationError("יש לבחור לפחות פרויקט אחד", null, "teams");
      }
    }
  }

  return (
    <Stack
      className="organization-request-buttons"
      direction="row"
      gap={2}
      alignItems="center"
      justifyContent="flex-end"
    >
      {isAidRequest && (
        <Button
          sx={{ borderRadius: "1rem" }}
          variant="outlined"
          type="button"
          onClick={handleDelete}
        >
          מחיקת הבקשה
        </Button>
      )}
      <Button
        sx={{ borderRadius: "1rem" }}
        variant="contained"
        type="submit"
        onClick={handleSave}
      >
        {isProgress ? (
          <CircularProgress size={25} style={{ color: "white" }} />
        ) : isAidRequest ? (
          "אישור הבקשה"
        ) : (
          "שמירת משימה"
        )}
      </Button>
      {isAidRequest && (
        <Button
          sx={{ borderRadius: "1rem" }}
          type="button"
          onClick={handleUpdate}
        >
          {isEditProgress ? (
            <CircularProgress size={25} style={{ color: "navy" }} />
          ) : (
            "שמירת שינויים"
          )}
        </Button>
      )}
    </Stack>
  );
};

export default OrganizationRequestButtons;
